import css_text from '/srv/www/htdocs/loc/app/styles/web_components/input_field.css';

const has_element_internals = !!(window.ElementInternals && 'attachInternals' in HTMLElement.prototype);

function get_shared_sheet() {
	if (get_shared_sheet.cached) {
		return get_shared_sheet.cached;
	}
	const sheet = new CSSStyleSheet();
	sheet.replaceSync(css_text);
	get_shared_sheet.cached = sheet;
	return sheet;
}

const ICON_IDS = {
	search: 1845,
	close: 1842,
	visibility: 1843,
	visibility_off: 1844,
};

function yimage_icon(id, pixel) {
	return yalwa.basics.yimage(id, {pixel: pixel ?? 24});
}

function get_icon(type) {
	const id = ICON_IDS[type];
	if (id) {
		return yimage_icon(id);
	}
}

export class YalwaInputField extends HTMLElement {

	static formAssociated = true;

	static get observedAttributes() {
		return ['type', 'size', 'disabled', 'readonly', 'error', 'required', 'chars', 'min', 'max', 'chars_label', 'name', 'value', 'placeholder', 'form', 'unit', 'unit_position'];
	}

	constructor() {
		super();
		this._internals = has_element_internals ? this.attachInternals() : null;
		this._form_proxy = null;
		this._password_visible = false;
		if (!this.shadowRoot) {
			this.attachShadow({mode: 'open'});
		}
		this.apply_css();
	}

	async apply_css() {
		await yalwa.basics.check_stylesheet_polyfill_needed();
		this.shadowRoot.adoptedStyleSheets = [get_shared_sheet()];
	}

	connectedCallback() {
		this.initialize_template();
		this._input = this.shadowRoot.querySelector('input');
		this._container = this.shadowRoot.querySelector('.container');
		this._leading_icon_wrapper = this.shadowRoot.querySelector('.leading_icon_wrapper');
		this._trailing_icon_wrapper = this.shadowRoot.querySelector('.trailing_icon_wrapper');
		this._char_count = this.shadowRoot.querySelector('.js-char_count');
		this._unit = this.shadowRoot.querySelector('.js-unit');

		this._wire_events();
		this._sync_icons();
		this._sync_input_native_type();
		this._sync_chars();
		this._sync_unit();
		this.render();

		if (this.hasAttribute('value')) {
			this.value = this.getAttribute('value');
		}
		if (this.hasAttribute('placeholder')) {
			this._input.placeholder = this.getAttribute('placeholder');
		}
		if (this.hasAttribute('disabled')) {
			this._input.disabled = true;
		}
		if (this.hasAttribute('readonly')) {
			this._input.readOnly = true;
		}

		jQuery(this).removeClass('invisible');
	}

	initialize_template() {
		if (!this.shadowRoot.firstChild) {
			const template = document.createElement('template');
			template.innerHTML = `
				<div class="wrapper" part="wrapper">
					<div class="header">
						<label class="label">
							<slot></slot>
						</label>
						<span class="char-count js-char_count" aria-live="polite" style="display:none"></span>
					</div>
					<div class="container container--md" part="container">
						<span class="icon leading_icon_wrapper"></span>
						<input part="control">
						<span class="unit js-unit" part="unit" style="display:none"></span>
						<span class="icon icon--trailing trailing_icon_wrapper"></span>
					</div>
					<slot name="helper"></slot>
				</div>
			`;
			this.shadowRoot.appendChild(template.content.cloneNode(true));
		}
	}

	attributeChangedCallback(name, old_value, new_value) {
		if (old_value === new_value) {
			return;
		}

		if (name === 'type') {
			this._sync_icons();
			this._sync_input_native_type();
		}

		if (name === 'size' && this._container) {
			this.render();
		}

		if (name === 'value' && this._input && new_value !== null) {
			const current = this._input.value;
			if (new_value !== current) {
				this._input.value = new_value;
			}
		}

		if (name === 'disabled' && this._input) {
			this._input.disabled = new_value !== null;
			this.setAttribute('tabindex', new_value !== null ? '-1' : '0');
		}

		if (name === 'readonly' && this._input) {
			this._input.readOnly = new_value !== null;
		}

		if (['name', 'value', 'disabled', 'readonly', 'form', 'error'].includes(name)) {
			this.sync_form_state();
		}

		if (name === 'placeholder' && this._input) {
			this._input.placeholder = new_value ?? '';
		}

		if (['chars', 'min', 'max', 'chars_label'].includes(name)) {
			this._sync_chars();
		}

		if (name === 'unit' || name === 'unit_position') {
			this._sync_unit();
		}
	}

	_sync_unit() {
		if (!this._unit || !this._input) {
			return;
		}
		const unit = this.getAttribute('unit') || '';
		this._unit.textContent = unit;
		this._unit.style.display = unit === '' ? 'none' : 'flex';
		if (this.getAttribute('unit_position') === 'prefix') {
			this._container.insertBefore(this._unit, this._input);
		} else {
			this._container.insertBefore(this._unit, this._trailing_icon_wrapper);
		}
	}

	render() {
		if (!this._container) {
			return;
		}

		const size = this.getAttribute('size') || 'md';
		this._container.className = `container container--${size}`;
	}

	_sync_icons() {
		if (!this._leading_icon_wrapper || !this._trailing_icon_wrapper) {
			return;
		}

		const type = this.getAttribute('type') || 'text';

		if (type === 'search') {
			this._leading_icon_wrapper.innerHTML = get_icon('search');
			this._leading_icon_wrapper.style.display = 'flex';
			this._update_trailing_icon_search();
		} else {
			this._leading_icon_wrapper.innerHTML = '';
			this._leading_icon_wrapper.style.display = 'none';
		}

		if (type === 'password') {
			this._update_trailing_icon_password();
			this._trailing_icon_wrapper.style.display = 'flex';
		} else if (type !== 'search') {
			this._trailing_icon_wrapper.innerHTML = '';
			this._trailing_icon_wrapper.style.display = 'none';
		}
	}

	_update_trailing_icon_password() {
		this._trailing_icon_wrapper.innerHTML = this._password_visible ? get_icon('visibility') : get_icon('visibility_off');
	}

	_update_trailing_icon_search() {
		if (this._input && this._input.value.length > 0) {
			this._trailing_icon_wrapper.innerHTML = get_icon('close');
			this._trailing_icon_wrapper.style.display = 'flex';
		} else {
			this._trailing_icon_wrapper.innerHTML = '';
			this._trailing_icon_wrapper.style.display = 'none';
		}
	}

	_sync_chars() {
		const max = parseInt(this.getAttribute('max'), 10);
		if (this._input) {
			if (!isNaN(max)) {
				this._input.maxLength = max;
			} else {
				this._input.removeAttribute('maxlength');
			}
		}

		if (!this._char_count || !this.hasAttribute('chars')) {
			if (this._char_count) {
				this._char_count.style.display = 'none';
			}
			return;
		}

		const current = this._input ? this._input.value.length : 0;
		const label = this.getAttribute('chars_label') || '';

		let count_text = String(current);
		if (!isNaN(max)) {
			count_text = current + '/' + max;
		}
		if (label) {
			count_text = label + ': ' + count_text;
		}

		this._char_count.textContent = count_text;
		this._char_count.style.display = 'flex';
	}

	_sync_input_native_type() {
		if (!this._input) {
			return;
		}
		const type = this.getAttribute('type') || 'text';
		if (type === 'password') {
			this._input.type = this._password_visible ? 'text' : 'password';
		} else if (type === 'search') {
			this._input.type = 'text';
		} else {
			this._input.type = type;
		}
	}

	_wire_events() {
		if (!this._input || !this._trailing_icon_wrapper) {
			return;
		}

		this._input.addEventListener('input', () => {
			this.sync_form_state();
			if (this.getAttribute('type') === 'search') {
				this._update_trailing_icon_search();
			}
			this._sync_chars();
			this.dispatchEvent(new Event('input', {bubbles: true}));
		});

		this._input.addEventListener('change', () => {
			this.dispatchEvent(new Event('change', {bubbles: true}));
		});

		this._trailing_icon_wrapper.addEventListener('click', (e) => {
			e.stopPropagation();
			const type = this.getAttribute('type') || 'text';

			if (type === 'password') {
				this._password_visible = !this._password_visible;
				this._sync_input_native_type();
				this._update_trailing_icon_password();
				this._input.focus();
			} else if (type === 'search') {
				if (this.readOnly) {
					return;
				}
				this._input.value = '';
				this._update_trailing_icon_search();
				this._sync_chars();
				this.sync_form_state();
				this._input.focus();
				this.dispatchEvent(new Event('input', {bubbles: true}));
				this.dispatchEvent(new Event('change', {bubbles: true}));
			}
		});
	}

	sync_form_state() {
		const value = this._input ? this._input.value : '';
		const name = this.getAttribute('name');

		if (has_element_internals && this._internals) {
			this._internals.setFormValue(name ? value : null);
			if (!this.readOnly && this.hasAttribute('error')) {
				const helper_slot = this.shadowRoot.querySelector('slot[name="helper"]');
				const helper_nodes = helper_slot ? helper_slot.assignedNodes() : [];
				const message = helper_nodes.map(n => n.textContent).join('').trim();
				this._internals.setValidity({customError: true}, message || 'Invalid input', this._input);
			} else {
				this._internals.setValidity({});
			}
		} else {
			this.ensure_form_proxy();
			if (!this._form_proxy) {
				return;
			}

			const id = this.getAttribute('form');
			const target_form = id ? document.getElementById(id) : this.closest('form');

			if (target_form && this._form_proxy.parentNode !== target_form) {
				target_form.appendChild(this._form_proxy);
			}

			if (name) {
				this._form_proxy.name = name;
				this._form_proxy.value = value;
				this._form_proxy.disabled = this.hasAttribute('disabled');
			} else {
				this._form_proxy.removeAttribute('name');
			}
		}
	}

	ensure_form_proxy() {
		if (this._form_proxy) {
			return;
		}
		const input = document.createElement('input');
		input.type = 'hidden';
		input.style.display = 'none';
		input.setAttribute('aria-hidden', 'true');

		const id = this.getAttribute('form');
		const target_form = id ? document.getElementById(id) : this.closest('form');
		(target_form ?? this).appendChild(input);

		this._form_proxy = input;
	}

	disconnectedCallback() {
		if (this._form_proxy && this._form_proxy.parentNode) {
			this._form_proxy.parentNode.removeChild(this._form_proxy);
		}
	}

	get value() {
		return this._input ? this._input.value : '';
	}

	set value(val) {
		const str = String(val ?? '');
		if (this._input) {
			this._input.value = str;
		}
		this.setAttribute('value', str);
		this.sync_form_state();
		if (this.getAttribute('type') === 'search') {
			this._update_trailing_icon_search();
		}
		this._sync_chars();
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(val) {
		if (val) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}

	get readOnly() {
		return this.hasAttribute('readonly');
	}

	set readOnly(val) {
		if (val) {
			this.setAttribute('readonly', '');
		} else {
			this.removeAttribute('readonly');
		}
	}

	get name() {
		return this.getAttribute('name');
	}

	set name(val) {
		if (val) {
			this.setAttribute('name', val);
		} else {
			this.removeAttribute('name');
		}
	}

	focus() {
		if (this._input) {
			this._input.focus();
		}
	}

	blur() {
		if (this._input) {
			this._input.blur();
		}
	}

	setCustomValidity(message) {
		if (message) {
			this.setAttribute('error', '');
			this._update_helper_slot(message);
		} else {
			this.removeAttribute('error');
			this._update_helper_slot('');
		}
		this.sync_form_state();
	}

	_update_helper_slot(message) {
		const helper_slot = this.shadowRoot.querySelector('slot[name="helper"]');
		if (!helper_slot) {
			return;
		}
		const slotted = helper_slot.assignedElements();
		if (slotted.length < 1) {
			return;
		}
		const inline = slotted[0].querySelector('yalwa-inline-message');
		if (inline) {
			inline.textContent = message;
		}
	}
}

customElements.define('yalwa-input-field', YalwaInputField);
